import {
  Button,
  BUTTON_TYPE,
  COLORS,
  COMMON_STYLES,
  CrossIcon,
  Spinner,
  TEXT_STYLES,
  TYPOGRAPHY,
  VARIANTS_STYLES,
} from '@dehaat/dds'
import React, { useContext, useState } from 'react'
import { View, Text, StyleSheet, Modal, Pressable } from 'react-native'
import { Crop, SowingRecord } from '../../models/crop'
import { ResponsiveContext } from 'app/context/ResponsiveUIContext'
import createSowingRecord from '../../apis/createSowingRecord'
import ToastAndroid from 'app/mocks/ToastAndroid'
import postFarmSowing from '../../apis/postFarmSowing'
import { Farm } from '../../models/farmer'
import { DIMENSION_HEIGHT } from 'app/utils/common'

import RecordRow from './RecordRow'
import CropCard2 from './CropCard2'
import Calender from './Calender'
import FarmList from './FarmList'

interface AddSowingRecordModalProps {
  isVisible: boolean
  farmerId: string
  crop: Crop
  onClose: () => void
  onSubmit: (record: SowingRecord) => void
}

const AddSowingRecordModal = ({
  isVisible,
  farmerId,
  crop,
  onClose,
  onSubmit,
}: AddSowingRecordModalProps) => {
  const { mediaStyles } = useContext(ResponsiveContext)
  const [loading, setLoading] = useState(false)
  const [step, setStep] = useState(1)

  const today = new Date()
  const [sowingDate, setSowingDate] = useState(
    today.toISOString().split('T')[0],
  )
  const [taggedFarm, setTaggedFarm] = useState<Farm>()
  const [sowingRecordId, setSowingRecordId] = useState<string>()

  const addSowingRecord = async () => {
    setLoading(true)
    const formattedDate = sowingDate.split('-').reverse().join('-')
    const response = await createSowingRecord(
      farmerId,
      crop.id,
      formattedDate,
      crop.is_transplanted ? 'TRANSPLANTING_DATE' : 'SOWING_DATE',
    )
    if (response) {
      setSowingRecordId(response.sowing_record_id)
    } else {
      ToastAndroid.show(
        'Something wait wrong. Please try again later.',
        ToastAndroid.SHORT,
      )
    }
    setLoading(false)
  }

  const tagFarmToSowingRecord = async () => {
    if (!taggedFarm) {
      return
    }
    setLoading(true)
    const response = await postFarmSowing(
      farmerId,
      sowingRecordId!,
      taggedFarm?.id!,
    )
    setLoading(false)

    if (!response) {
      throw new Error('unable to tag farm')
    }
  }

  const handleNext = () => {
    if (step === 1) {
      addSowingRecord().then(() => {
        setStep(prev => prev + 1)
      })
    }
    if (step === 2) {
      tagFarmToSowingRecord()
        .then(() =>
          onSubmit({
            sowing_record_id: sowingRecordId!,
            crop,
            sowing_date: sowingDate,
            type: crop.is_transplanted ? 'TRANSPLANTING_DATE' : 'SOWING_DATE',
            farm_name: taggedFarm?.name ?? null,
          }),
        )
        .catch(() => {
          ToastAndroid.show(
            'Something went wrong. Please try again later.',
            ToastAndroid.SHORT,
          )
        })
    }
  }

  const handleBack = () => {
    if (step > 1) {
      setStep(prev => prev - 1)
    } else onClose()
  }

  return (
    <Modal visible={isVisible}>
      {loading && <Spinner type="on-screen" />}
      {isVisible ? (
        <View
          style={[
            COMMON_STYLES.fullHeight,
            mediaStyles({ desktopStyle: COMMON_STYLES.row }),
          ]}
        >
          <View
            style={[
              COMMON_STYLES.pv2,
              mediaStyles({
                desktopStyle: [styles.selectedCropContainer, styles['ph5/2']],
                mobileStyle: COMMON_STYLES.ph1,
              }),
            ]}
          >
            <Text
              style={[
                TYPOGRAPHY.HEADING3,
                VARIANTS_STYLES.EMPHASIZED,
                TEXT_STYLES.textNeutral90,
              ]}
            >
              Selected Ongoing Crop
            </Text>

            <CropCard2 cropName={crop.name} cropImageUrl={crop.image_url} />
            <RecordRow label={'Crop'} value={crop.name} />
            <RecordRow
              label={crop.is_transplanted ? 'Plantation Date' : 'Sowing Date'}
              value={sowingDate}
            />
            <RecordRow label={'Farm'} value={taggedFarm?.name} />
          </View>

          <View
            style={[
              mediaStyles({
                desktopStyle: styles.divider,
                mobileStyle: styles.dividerHz,
              }),
            ]}
          />
          <View
            style={[
              COMMON_STYLES.pt2,
              COMMON_STYLES.flex1,
              mediaStyles({
                desktopStyle: [styles.selectedCropContainer, styles['ph5/2']],
                mobileStyle: COMMON_STYLES.ph1,
              }),
            ]}
          >
            {step === 1 && (
              <>
                <View
                  style={[
                    COMMON_STYLES.row,
                    COMMON_STYLES.fullWidth,
                    COMMON_STYLES.justifyBetween,
                  ]}
                >
                  <Text
                    style={[
                      TYPOGRAPHY.TEXT1,
                      VARIANTS_STYLES.MEDIUM,
                      TEXT_STYLES.textNeutral100,
                    ]}
                  >
                    {crop.is_transplanted
                      ? 'Add transplantation date*'
                      : 'Add sowing date*'}
                  </Text>
                  <Pressable onPress={onClose}>
                    <CrossIcon height={32} width={32} />
                  </Pressable>
                </View>
                <Calender
                  date={sowingDate ? new Date(sowingDate).getTime() : null}
                  onDateSelect={setSowingDate}
                  style={COMMON_STYLES.mv1}
                  futureDateDisabled
                />
              </>
            )}
            {step === 2 && (
              <>
                <View
                  style={[
                    COMMON_STYLES.row,
                    COMMON_STYLES.fullWidth,
                    COMMON_STYLES.justifyBetween,
                  ]}
                >
                  <Text
                    style={[
                      TYPOGRAPHY.TEXT1,
                      VARIANTS_STYLES.MEDIUM,
                      TEXT_STYLES.textNeutral100,
                    ]}
                  >
                    In which farm this crop is growing
                  </Text>
                  <Pressable onPress={onClose}>
                    <CrossIcon height={32} width={32} />
                  </Pressable>
                </View>
                <FarmList
                  farmerId={farmerId}
                  selectedFarmId={taggedFarm?.id}
                  onFarmSelect={setTaggedFarm}
                />
              </>
            )}
            <View
              style={[
                COMMON_STYLES.row,
                COMMON_STYLES.justifyEnd,
                COMMON_STYLES.fullWidth,
                COMMON_STYLES.flex1,
                COMMON_STYLES.alignItemsEnd,
                COMMON_STYLES.p1,
              ]}
            >
              <Button
                onClick={handleBack}
                text={'Back'}
                type={BUTTON_TYPE.SECONDARY}
                buttonStyle={styles.actionBtn}
              />
              <Button
                text={step === 2 ? 'Save' : 'Next'}
                onClick={handleNext}
                buttonStyle={[styles.actionBtn, COMMON_STYLES.ml1]}
              />
            </View>
          </View>
        </View>
      ) : null}
    </Modal>
  )
}

const styles = StyleSheet.create({
  divider: {
    borderRightWidth: 1,
    borderColor: COLORS.neutral20,
    height: DIMENSION_HEIGHT,
  },
  dividerHz: {
    borderBottomWidth: 1,
    borderColor: COLORS.neutral20,
  },
  'ph5/2': {
    paddingHorizontal: 40,
  },

  selectedCropContainer: {
    width: 400,
  },
  actionBtn: {
    width: '25%',
    height: 56,
  },
})

export default AddSowingRecordModal
